// The town (western_town.png through the story): see town_build.ts for the layout and
// town_kit.ts for the atlas materials. Three draws plus their shadow casters:
// solid atlas mesh, alpha-cut atlas mesh (awnings, posters, bunting), tree canopies, wires,
// and the hazed skyline backdrop.

import * as THREE from 'three';
import type { World } from '../../../sim/world';
import type { ViewCtx } from '../contracts';
import { withHaze } from '../materials';
import { PAL } from '../palette';
import { buildBackdrop, buildTownPartsSteps } from './town_build';
import { atlasDepthMaterial, atlasMaterial, lampMap, loadAtlas } from './town_kit';
import { canopyDepthMaterial, canopyGeometry, canopyMaterial, loadLeaves } from './town_canopy';
import { Clock } from '../warm';
import { RuinView } from './town_burn';
import { BreachView } from './town_breach';

/** The whole town as one object: WorldView adds `group` and calls update/dispose. */
export class Town {
  readonly group = new THREE.Group();
  private disposables: { dispose(): void }[] = [];
  /** the skyline backdrop is a matte for the street: shown only while the camera is in the street */
  private back: THREE.Mesh | null = null;
  private street = { x0: 0, x1: 0, z: 0, hw: 0, y: 0 };
  /** in the street the sun's shadow frustum is tightened (crisper building shadows), restored outside */
  private sunCam: THREE.OrthographicCamera | null = null;
  private sunShadow: THREE.DirectionalLightShadow | null = null;
  private sunRadius = 1;
  private sunExt = 0;
  private tight = false;
  private inkU = { uInkPx: { value: 1.7 }, uInkRes: { value: 720 } };
  private renderer: THREE.WebGLRenderer;

  private world: World;
  private live: World;
  private ctx: ViewCtx;
  /** scorch, fire and collapse (town_burn.ts); set by steps() */
  ruin!: RuinView;
  /** holes flown through the walls (town_breach.ts) */
  private breach: BreachView | null = null;
  /** Empty until steps() has been stepped through (the island build does it in slices).
   *  `live` is the island being played (its damage drives the burn); `world` is it as generated. */
  constructor(world: World, ctx: ViewCtx, live: World = world) {
    this.renderer = ctx.renderer;
    this.world = world; this.live = live; this.ctx = ctx;
    this.group.name = 'town';
  }

  *steps(pause: (at: string) => Generator<unknown, void, unknown>): Generator<unknown, void, unknown> {
    const world = this.world, ctx = this.ctx;
    const q = Math.max(0, Math.min(3, ctx.quality | 0));
    const parts = yield* buildTownPartsSteps(world, q, pause);
    yield* pause('town:atlas');
    const tex = loadAtlas(q, ctx.renderer.capabilities.getMaxAnisotropy());

    // night: every lantern pools warm light on the street and the fronts around it
    const lamps = lampMap(parts.cut, parts.solid);
    yield* pause('town:solid');
    const solidGeo = parts.solid.build();
    this.ruin = new RuinView(this.live, parts.ruin);
    this.ruin.rate = [0.4, 0.7, 1, 1.2][q];
    this.ruin.bindGeometry(solidGeo, 'solid');
    const solidMat = this.ruin.patch(atlasMaterial(tex, q, { lamps }));
    const solid = new THREE.Mesh(solidGeo, solidMat);
    solid.name = 'townSolid';
    solid.castShadow = q > 0; solid.receiveShadow = true;

    // print ink: an inverted hull round every building, roof edge and trim piece. It pushes out
    // along position-averaged normals (so box corners close) by a width held at ~1.7 px on screen.
    yield* pause('town:hull');
    const hullGeo = yield* hullGeometry(solidGeo, new Clock(pause));
    const hullMat = this.ruin.patch(inkMaterial(this.inkU), true);
    hullGeo.setAttribute('bld', solidGeo.getAttribute('bld'));
    const hull = new THREE.Mesh(hullGeo, hullMat);
    hull.name = 'townInk';
    this.group.add(hull);
    this.disposables.push(hullGeo, hullMat);

    yield* pause('town:cut');
    const cutGeo = parts.cut.build();
    this.ruin.bindGeometry(cutGeo, 'cut');
    const cutMat = this.ruin.patch(atlasMaterial(tex, q, { cutout: true, lamps }));
    const cut = new THREE.Mesh(cutGeo, cutMat);
    cut.name = 'townCut';
    cut.receiveShadow = true;
    cut.castShadow = q >= 2;
    const depth = atlasDepthMaterial(tex);
    cut.customDepthMaterial = depth;

    // tree canopies: clumped leaf masses from the landscape lane's leaves.webp
    yield* pause('town:canopy');
    if (parts.canopies.length) {
      const leaves = loadLeaves(q, ctx.renderer.capabilities.getMaxAnisotropy());
      const canGeo = canopyGeometry(parts.canopies, q);
      const canMat = canopyMaterial(leaves);
      const canDepth = canopyDepthMaterial(leaves);
      const can = new THREE.Mesh(canGeo, canMat);
      can.name = 'townCanopy';
      can.castShadow = q > 0; can.receiveShadow = true;
      can.customDepthMaterial = canDepth;
      this.group.add(can);
      this.disposables.push(leaves, canGeo, canMat, canDepth);
    }

    const wireGeo = new THREE.BufferGeometry();
    wireGeo.setAttribute('position', new THREE.Float32BufferAttribute(parts.wires, 3));
    wireGeo.computeBoundingSphere();
    const wireMat = withHaze(new THREE.LineBasicMaterial({ color: new THREE.Color(PAL.inkDark).multiplyScalar(0.55) }));
    const wires = new THREE.LineSegments(wireGeo, wireMat);
    wires.name = 'townWires';

    // the town goes on into the haze past the gate: layered skyline silhouettes over the void
    yield* pause('town:backdrop');
    const backGeo = buildBackdrop(world);
    const backMat = withHaze(new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide }), 0.62);
    // the matte is unlit paint: at night it sinks to moonlit slate with the town (haze adds the rest)
    { const prev = backMat.onBeforeCompile;
      backMat.onBeforeCompile = (sh, r) => {
        prev.call(backMat, sh, r);
        sh.fragmentShader = sh.fragmentShader.replace('#include <opaque_fragment>',
          'outgoingLight *= mix( vec3( 1.0 ), vec3( 0.2, 0.24, 0.38 ), uNight );\n#include <opaque_fragment>');
      };
      backMat.customProgramCacheKey = () => 'townBackdropNight'; }
    const back = new THREE.Mesh(backGeo, backMat);
    back.name = 'townBackdrop';
    this.back = back;
    const t = world.town;
    this.street = { x0: t.x0 - 14, x1: t.x1 + 4, z: t.z, hw: t.halfWidth + 9, y: t.y + 14 };
    if (q >= 2) ctx.scene.traverse((o) => {
      const l = o as THREE.DirectionalLight;
      if (!this.sunCam && l.isDirectionalLight && l.castShadow) {
        // the untightened frustum: the island on screen may be holding it tight while this one builds
        const ud = l.userData as { ext?: number; radius?: number };
        ud.ext ??= l.shadow.camera.right; ud.radius ??= l.shadow.radius;
        this.sunCam = l.shadow.camera; this.sunExt = ud.ext; this.sunShadow = l.shadow; this.sunRadius = ud.radius;
      }
    });
    this.group.add(back);
    this.disposables.push(backGeo, backMat);

    this.breach = new BreachView(this.live, solidGeo, solidMat, this.ruin.holeRows, this.ruin.holeFits);
    this.group.add(this.breach.group);
    this.group.add(solid, cut, wires);
    this.disposables.push(solidGeo, solidMat, cutGeo, cutMat, depth, wireGeo, wireMat, tex.map, tex.normal);
    if (lamps.count) this.disposables.push(lamps.tex);
  }

  /** the paint colour of the house wall at (x, y, z) facing (nx, nz), or null (town_breach.ts) */
  breachPaint(x: number, y: number, z: number, nx: number, nz: number) { return this.breach?.paintNear(x, y, z, nx, nz) ?? null; }

  update(dt: number, time: number, camPos: THREE.Vector3): void {
    this.inkU.uInkRes.value = this.renderer.domElement.height || 720;
    if (this.ruin) { this.ruin.cam.copy(camPos); this.ruin.update(dt, time); }
    this.breach?.update();
    if (!this.back) return;
    const s = this.street;
    // from outside the town (world views, the approach) the matte would read as floating card
    const inStreet = camPos.x > s.x0 && camPos.x < s.x1 && Math.abs(camPos.z - s.z) < s.hw && camPos.y < s.y;
    this.back.visible = inStreet;
    const sc = this.sunCam;
    if (sc && inStreet !== this.tight) {
      this.tight = inStreet;
      const e = inStreet ? Math.min(this.sunExt, 26) : this.sunExt;
      sc.left = -e; sc.right = e; sc.top = e; sc.bottom = -e;
      sc.updateProjectionMatrix();
      // a tighter PCF kernel in the street: pole, awning and tree shadows land as crisp shapes
      if (this.sunShadow) this.sunShadow.radius = inStreet ? 0.5 : this.sunRadius;
    }
  }

  dispose(): void {
    this.group.removeFromParent();
    // hand the next island the sun's frustum as it found it
    if (this.tight && this.sunCam) {
      const e = this.sunExt, sc = this.sunCam;
      sc.left = -e; sc.right = e; sc.top = e; sc.bottom = -e; sc.updateProjectionMatrix();
      if (this.sunShadow) this.sunShadow.radius = this.sunRadius;
      this.tight = false;
    }
    for (const d of this.disposables) d.dispose();
    this.breach?.dispose();
  }
}

/** The hull shares the solid mesh's positions; aOut is the normal averaged over every vertex at the
 *  same spot, so the shell closes over hard box corners instead of splitting into loose faces. */
function* hullGeometry(src: THREE.BufferGeometry, clk: Clock): Generator<unknown, THREE.BufferGeometry, unknown> {
  const pos = src.getAttribute('position') as THREE.BufferAttribute;
  const nrm = src.getAttribute('normal') as THREE.BufferAttribute;
  const n = pos.count;
  const ids = new Int32Array(n);
  const map = new Map<string, number>();
  const acc: number[] = [];
  for (let i = 0; i < n; i++) {
    if ((i & 1023) === 0) yield* clk.tick('town:hull');
    const k = `${Math.round(pos.getX(i) * 200)},${Math.round(pos.getY(i) * 200)},${Math.round(pos.getZ(i) * 200)}`;
    let id = map.get(k);
    if (id === undefined) { id = acc.length / 3; map.set(k, id); acc.push(0, 0, 0); }
    ids[i] = id;
    acc[id * 3] += nrm.getX(i); acc[id * 3 + 1] += nrm.getY(i); acc[id * 3 + 2] += nrm.getZ(i);
  }
  const out = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    const id = ids[i];
    let x = acc[id * 3], y = acc[id * 3 + 1], z = acc[id * 3 + 2];
    const l = Math.hypot(x, y, z);
    if (l < 1e-4) { x = nrm.getX(i); y = nrm.getY(i); z = nrm.getZ(i); } else { x /= l; y /= l; z /= l; }
    out[i * 3] = x; out[i * 3 + 1] = y; out[i * 3 + 2] = z;
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', pos);
  g.setAttribute('aOut', new THREE.BufferAttribute(out, 3));
  if (src.index) g.setIndex(src.index);
  g.boundingSphere = src.boundingSphere?.clone() ?? null;
  if (!g.boundingSphere) g.computeBoundingSphere();
  return g;
}

/** Warm dark print ink (#2a2018), back faces only, width in screen pixels (clamped in metres). */
function inkMaterial(u: Record<string, THREE.IUniform>) {
  const m = new THREE.MeshBasicMaterial({ color: new THREE.Color(0x2a2018), side: THREE.BackSide });
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, u);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute vec3 aOut;\nuniform float uInkPx; uniform float uInkRes;')
      .replace('#include <begin_vertex>', /* glsl */ `
        vec3 transformed = vec3( position );
        {
          float dv = max( 0.5, -( modelViewMatrix * vec4( position, 1.0 ) ).z );
          // metres per pixel at this depth, times the wanted width; a little wobble reads as a print edge
          float wob = 0.8 + 0.4 * fract( sin( dot( position, vec3( 12.9898, 78.233, 37.719 ) ) ) * 43758.5453 );
          float w = clamp( uInkPx * 2.0 * dv / ( uInkRes * projectionMatrix[ 1 ][ 1 ] ), 0.012, 0.14 ) * wob;
          transformed += aOut * w;
        }
      `);
  };
  withHaze(m, 0.6);
  m.customProgramCacheKey = () => 'townInk';
  return m;
}
